<template>
  <!-- The Quest (quest journey 2026-10-27; recipient polish 2026-11-03,
       DESIGN §18, §20.3, §20.5, §20.6): one level in focus, the built sky
       drawing on the left, the focused level's steps and what it unlocks on
       the right. Every star of a level must be lit to open what it holds.
       Reveals play where they were earned; here, the Sun's ceremony and the
       banner of a level drawn. -->
  <div class="quest-panel" :class="{ wide }" data-testid="quest-panel">
    <div class="quest-tabs">
      <button class="quest-tab active">Quest</button>
    </div>

    <div v-if="loading && !j" class="quest-loading"><span class="q-pulse"></span> Gathering your sky…</div>
    <div v-else-if="!j" class="quest-loading">Your sky isn't here yet. Try again in a moment.</div>

    <div v-else class="qp-body">
      <div class="qp-left">
        <QuestLevels ref="levelsRef" :journey="j" :focus="focus" :fx="fx" :just-done="justDone" :bright="ceremony" :reduced="reduced"
                     :pfx="narrow ? 'qfn' : 'qf'" @focus="setFocus" @star="onSkyStar">
          <template #banner>
            <div v-if="banner" class="qp-banner" role="status" data-testid="quest-banner">
              <div class="qp-eyebrow">{{ banner.eyebrow }}</div>
              <b>{{ banner.title }}</b>
              <p>{{ banner.text }}</p>
              <span v-if="banner.carats" class="qp-banner-ct">✦ {{ banner.carats }}</span>
            </div>
          </template>
        </QuestLevels>
      </div>

      <div class="qp-side" data-testid="quest-side">
        <!-- The one welcome for money (if they closed the Community's hero before claiming). -->
        <div v-if="j.welcome" class="qp-welcome" data-testid="quest-welcome">
          <div class="qp-eyebrow">{{ j.welcome.title }}</div>
          <div class="qp-amt">{{ j.welcome.total.toLocaleString() }} ✦</div>
          <button class="qp-btn qp-btn-inline" :disabled="claimingAll" data-testid="quest-welcome-claim" @click="claimAll">{{ claimingAll ? '…' : j.welcome.label }}</button>
        </div>
        <div v-else-if="j.claim" class="qp-claimrow" data-testid="quest-stardust">
          <span>Stars waiting for you</span>
          <button class="qp-claim" :disabled="claimingAll" data-testid="quest-claim-all" @click="claimAll">{{ claimingAll ? '…' : `Claim ${claimShown} ✦` }}</button>
        </div>
        <!-- Today's gift star (mystic-page 2026-10-29): one daily row; its ✦ is in the Claim above. -->
        <GiftStarRow v-if="j.giftStar" :star="j.giftStar" :landing="giftLanding" />

        <!-- Tonight · Cast your star: at the top every night from the Studio's opening until cast (§18, §20.6). -->
        <div v-if="tonight" class="qp-card" data-testid="quest-tonight">
          <div class="qp-eyebrow">{{ tonight.eyebrow }}</div>
          <h3 class="qp-card-title">{{ tonight.title }}</h3>
          <p class="qp-card-body">{{ tonight.body }}</p>
          <button class="qp-btn" data-testid="quest-tonight-cast" @click="doButton(tonight.button, 'tonight')">{{ tonight.button.label }}</button>
        </div>

        <!-- The Sun: the seal broke; its card waits here and never ambushes. -->
        <div v-if="unsealShown" class="qp-unseal" role="dialog" :aria-label="j.unseal.title" data-testid="quest-unlock">
          <svg viewBox="0 0 84 84" aria-hidden="true" class="qp-unseal-sun"><circle cx="42" cy="42" r="30" fill="url(#qp-unseal-halo)" opacity=".6" />
            <defs><radialGradient id="qp-unseal-halo"><stop offset="0" stop-color="#ffe7b5" stop-opacity=".6" /><stop offset="1" stop-color="#c9a94e" stop-opacity="0" /></radialGradient></defs>
            <circle cx="42" cy="42" r="14" fill="#ffd98a" />
            <line v-for="i in 8" :key="i" :x1="42 + 19 * Math.cos(i * Math.PI / 4)" :y1="42 + 19 * Math.sin(i * Math.PI / 4)" :x2="42 + 30 * Math.cos(i * Math.PI / 4)" :y2="42 + 30 * Math.sin(i * Math.PI / 4)" stroke="#f0d9a3" stroke-width="1.4" /></svg>
          <h3 class="qp-unseal-title">{{ j.unseal.title }}</h3>
          <p v-if="j.unseal.sub" class="qp-unseal-body">{{ j.unseal.sub }}.</p>
          <button class="qp-btn" data-testid="quest-unlock-button" :disabled="claimingStudio" @click="openStudio">{{ claimingStudio ? 'Opening…' : (j.unseal.button ? j.unseal.button.label : 'Open the Studio') }}</button>
          <button class="qp-quiet" data-testid="quest-unlock-later" @click="unsealLater = true">Later</button>
        </div>

        <!-- The Mystic's one line back to what they said. -->
        <p v-if="landedAck" class="qp-landed-ack" data-testid="quest-landed-ack">{{ landedAck }}</p>

        <!-- The focused level: its place, what it unlocks, its steps numbered like its stars. -->
        <section v-if="lp" class="qp-level" :class="{ teaser: lp.teaser }" :aria-label="lp.name" data-testid="quest-level-panel">
          <div class="qp-head">
            <span class="qp-eyebrow" data-testid="quest-level-eyebrow">{{ lp.eyebrow }}</span>
            <span v-if="j.money" class="qp-bal" data-testid="quest-balance">✦ {{ balance }}</span>
          </div>
          <h2 class="qp-level-name" data-testid="quest-level-name">{{ lp.name }}</h2>
          <p class="qp-level-line" data-testid="quest-level-line">{{ lp.line }}</p>
          <template v-if="lp.teaser">
            <ul class="qp-coming"><li v-for="c in lp.coming" :key="c">{{ c }}</li></ul>
            <p v-if="lp.drawn" class="qp-sline">Your sky is drawn. A reading made for someone is always open.</p>
            <!-- A finished sky's Next (the server's "Read for someone"), kept from the built Next card. -->
            <button v-if="lp.drawn && finishedNext && finishedNext.button" class="qp-btn" data-testid="quest-finished-go" @click="doButton(finishedNext.button, 'step')">{{ finishedNext.button.label }}</button>
          </template>
          <template v-else>
            <div class="qp-unl" data-testid="quest-level-unlocks">
              <span class="qp-unl-ic" aria-hidden="true"><i v-if="UNLOCK_ICON[lp.tab]" :class="['fa-solid', UNLOCK_ICON[lp.tab]]"></i><template v-else>✦</template></span>
              <div><b>Unlocks</b><span class="qp-unl-t">{{ lp.unlock.text }}</span></div>
              <span :class="['qp-unl-s', { ok: lp.unlock.open }]" data-testid="quest-level-progress">{{ lp.unlock.state }}</span>
            </div>
            <div v-if="lp.reward" class="qp-rew" data-testid="quest-level-reward">{{ lp.reward }}</div>
            <ol class="qp-steps" data-testid="quest-steps">
              <li v-for="(s, i) in lp.steps" :key="s.key" :class="['qs', s.state, { flash: flashRow === s.key }]" :data-testid="`quest-step-${s.key}`" :data-row="s.key">
                <span class="qs-n" aria-hidden="true">{{ i + 1 }}</span>
                <div class="qs-main">
                  <button v-if="s.state === 'lit' && s.moment" type="button" class="qs-open" :data-testid="`quest-step-open-${s.key}`" @click="openStar(s.key)">
                    <b>{{ s.label }}</b><small>{{ s.word }}</small>
                  </button>
                  <template v-else>
                    <b>{{ s.label }}</b>
                    <small v-if="s.state === 'lit'">{{ s.word }}</small>
                    <p v-else-if="s.body">{{ s.body }}</p>
                  </template>
                  <p v-if="s.sub && s.state !== 'lit'" class="qs-sub">{{ s.sub }}</p>
                  <span class="sr-only">{{ s.state === 'lit' ? '(done)' : s.state === 'next' ? '(next)' : '' }}</span>
                </div>
                <div v-if="s.state !== 'lit' || s.coin" class="qs-act">
                  <div v-if="landing && landing.from === `step:${s.key}`" class="qp-landed" data-testid="quest-landed-box">
                    <textarea ref="landedEl" v-model="landedText" class="qp-note" rows="3" maxlength="2000" :placeholder="LANDED_PROMPT"
                              aria-label="How it felt, in your own words" data-testid="quest-landed-text"></textarea>
                    <button class="qp-btn" :disabled="acting || !landedText.trim()" data-testid="quest-landed-save" @click="saveLanded">{{ acting ? '…' : 'Save' }}</button>
                  </div>
                  <template v-else-if="s.state !== 'lit'">
                    <button v-if="s.button" :class="['qp-btn', 'qs-go', { quiet: s.state !== 'next' }]" :data-testid="`quest-step-go-${s.key}`" @click="doButton(s.button, 'step', s.key)">{{ s.button.label }}</button>
                    <button v-if="s.quiet" class="qp-quiet qs-quiet" :data-testid="`quest-step-quiet-${s.key}`" @click="doButton(s.quiet, 'step', s.key)">{{ s.quiet.label }}</button>
                    <span v-if="s.later && !s.button" class="qs-later" :data-testid="`quest-step-later-${s.key}`">{{ s.later }}</span>
                    <span v-if="s.notYet" class="qs-not">Not yet</span>
                  </template>
                  <span v-if="s.coin" class="qs-coin">{{ s.coin }}</span>
                </div>
              </li>
            </ol>
            <template v-if="lp.along.length">
              <div class="qp-along">Along the way · not needed to unlock</div>
              <ol class="qp-steps">
                <li v-for="s in lp.along" :key="s.key" :class="['qs', s.state === 'lit' ? 'lit' : 'not', { flash: flashRow === s.key }]" :data-testid="`quest-step-${s.key}`" :data-row="s.key">
                  <span class="qs-n" aria-hidden="true">✧</span>
                  <div class="qs-main">
                    <button v-if="s.state === 'lit' && s.moment" type="button" class="qs-open" :data-testid="`quest-step-open-${s.key}`" @click="openStar(s.key)">
                      <b>{{ s.label }}</b><small>{{ s.word }}</small>
                    </button>
                    <template v-else>
                      <b>{{ s.label }}</b>
                      <small v-if="s.state === 'lit'">{{ s.word }}</small>
                      <p v-else-if="s.body">{{ s.body }}</p>
                    </template>
                    <p v-if="s.sub && s.state !== 'lit'" class="qs-sub">{{ s.sub }}</p>
                  </div>
                  <div v-if="(s.state !== 'lit' && (s.button || s.later)) || s.coin || (s.state === 'lit' && s.key === 'aw_letter')" class="qs-act">
                    <template v-if="s.state !== 'lit'">
                      <button v-if="s.button" class="qp-btn qs-go quiet" :data-testid="`quest-step-go-${s.key}`" @click="doButton(s.button, 'step', s.key)">{{ s.button.label }}</button>
                      <span v-if="s.later && !s.button" class="qs-later">{{ s.later }}</span>
                    </template>
                    <button v-else-if="s.key === 'aw_letter'" class="qp-btn qs-go quiet" :data-testid="`quest-step-go-${s.key}`" @click="emitAct({ type: 'letter' })">Read the letter</button>
                    <span v-if="s.coin" class="qs-coin">{{ s.coin }}</span>
                  </div>
                </li>
              </ol>
            </template>
          </template>
          <nav class="qp-nav" aria-label="Levels">
            <button type="button" data-testid="quest-level-prev" :disabled="!lp.prev" @click="lp.prev && setFocus(lp.prev.key)">‹ {{ lp.prev ? lp.prev.name : '' }}</button>
            <button type="button" data-testid="quest-level-next" :disabled="!lp.next" @click="lp.next && setFocus(lp.next.key)">{{ lp.next ? lp.next.name : '' }} ›</button>
          </nav>
        </section>
      </div>

      <!-- A star's sheet: the moment that lit it (Saturn: what is coming). -->
      <div v-if="sheet" class="qp-sheet-wrap" data-testid="quest-star-sheet" @click.self="sheet = null">
        <div class="qp-sheet" role="dialog" :aria-label="sheet.title">
          <button class="qp-sheet-x" aria-label="Close" @click="sheet = null">&times;</button>
          <div class="qp-eyebrow">{{ sheet.section }}<template v-if="sheet.along"><i class="qp-dot">·</i>Along the way</template></div>
          <h3 class="qp-sheet-title">{{ sheet.title }}</h3>
          <template v-if="sheet.coming">
            <ul class="qp-coming"><li v-for="c in sheet.coming" :key="c">{{ c }}</li></ul>
            <p class="qp-only">Its rings light when each one arrives.</p>
          </template>
          <template v-else-if="sheet.lit && sheet.moment">
            <div class="qp-mo" data-testid="quest-moment">
              <div v-if="momentDay(sheet.moment.at)" class="qp-mo-date">{{ momentDay(sheet.moment.at) }}</div>
              <div class="qp-mo-line" data-testid="moment-line"><template v-for="(seg, i) in momentSegments(sheet.moment.line)" :key="i"><b v-if="seg.b">{{ seg.text }}</b><i v-else-if="seg.i">{{ seg.text }}</i><template v-else>{{ seg.text }}</template></template></div>
              <p v-if="sheet.moment.quote" class="qp-mo-quote">“{{ sheet.moment.quote }}”</p>
            </div>
            <p v-if="sheet.ring" class="qp-ringline" data-testid="quest-star-ring">{{ sheet.ring.who }} wrote back: <i>{{ sheet.ring.word }}</i></p>
            <p class="qp-only">Only you see this.</p>
          </template>
          <template v-else>
            <p v-if="sheet.body" class="qp-sline">{{ sheet.body }}</p>
            <p v-if="sheet.sub" class="qp-sline qp-sub-line" data-testid="quest-star-sub">{{ sheet.sub }}</p>
          </template>
          <button v-if="sheet.claim" class="qp-btn" data-testid="quest-star-claim" :disabled="!!claiming" @click="claimOne(sheet.claim.key)">{{ claiming ? '…' : `Claim ${sheet.claim.payout} ✦` }}</button>
          <button v-if="sheet.button" class="qp-btn" data-testid="quest-star-go" @click="doButton(sheet.button, 'star')">{{ sheet.button.label }}</button>
          <button v-if="sheet.quiet" class="qp-quiet" data-testid="quest-star-quiet" @click="doButton(sheet.quiet, 'star')">{{ sheet.quiet.label }}</button>
        </div>
      </div>

      <!-- "Your sky made you this" (§15, §20.4): the name first, the portrait as soon as it is painted. -->
      <div v-if="keeping" class="qp-sheet-wrap" data-testid="quest-keep-sheet" @click.self="keeping = false">
        <div class="qp-sheet" role="dialog" aria-label="Your sky made you this">
          <button class="qp-sheet-x" aria-label="Close" @click="keeping = false">&times;</button>
          <!-- Recipient polish §15, §20.4: the name first, the portrait as it paints. -->
          <SkyKeepCard :gift="skyGift || {}" :busy="acting" @keep="keepSky" @later="keeping = false"
                       @rename="keeping = false; emitAct({ type: 'mirror' })" @repaint="keeping = false; emitAct({ type: 'mirror' })" />
        </div>
      </div>

      <!-- "Say thank you" to the Mystic of their own reading (Mystic AIda counts). -->
      <div v-if="thanking" class="qp-sheet-wrap" data-testid="quest-thank-sheet" @click.self="thanking = null">
        <div class="qp-sheet" role="dialog" aria-label="Say thank you">
          <button class="qp-sheet-x" aria-label="Close" @click="thanking = null">&times;</button>
          <div class="qp-eyebrow">Venus</div>
          <h3 class="qp-sheet-title">Say thank you</h3>
          <p class="qp-sline">Tell Mystic AIda what the reading meant.</p>
          <div class="qp-chips">
            <button v-for="w in THANKS" :key="w" type="button" :class="['qp-chip', { on: thankNote === w }]" @click="thankNote = w">{{ w }}</button>
          </div>
          <textarea v-model="thankNote" class="qp-note" maxlength="280" rows="2" placeholder="Or in your own words" aria-label="Your thanks"></textarea>
          <button class="qp-btn" :disabled="acting || !thankNote.trim()" data-testid="quest-thank-send" @click="sendThanks">{{ acting ? '…' : 'Send thanks' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { store } from '@/store'
import { getPlatformQuests, claimQuest, claimAllQuests, navSeen, questAct } from '@/api-service'
import GiftStarRow from '@/components/quest/GiftStarRow.vue'
import SkyKeepCard from '@/components/quest/SkyKeepCard.vue'
import QuestLevels from '@/components/quest/QuestLevels.vue'
import { track } from '@/utils/analytics'
import { GIFT_STAR_SEEN_KEY, readJourney, starSheet, actionFor, findStar, sectionKey, momentSegments, momentDay,
  levelPanel, levelOrder, tonightCard, levelBanner, focusDefault, nextCard } from '@/utils/quest-sky'

const props = defineProps({
  // ?panel=quest&star=<key>: focus that star's level and ring it once.
  star: { type: String, default: '' },
  stage: { type: Number, default: 0 },
  // Hosted in the room's 640px sheet: the phone layout at any width.
  narrow: { type: Boolean, default: false },
  // nav.sky_gift (API §5): the name and round portrait the birthday made.
  skyGift: { type: Object, default: null },
})
const emit = defineEmits(['close', 'go-to-studio', 'navigate', 'act', 'unclaimed', 'mystic-unlocked', 'entry-carats', 'nav'])

const THANKS = ['Exactly right', 'It helped', 'I needed that']
// "Say how it felt" (§20.3): any words count, "it didn't land" included.
const LANDED_PROMPT = 'A few words about the reading, even “it didn’t land”. There’s no wrong answer.'
const CEREMONY_MS = 5600
const CEREMONY_REDUCED_MS = 1000
const BANNER_MS = 2600
const BANNER_REDUCED_MS = 1400
// The tab a level opens, as the bar draws it.
const UNLOCK_ICON = { journal: 'fa-book-open', altar: 'fa-eye', community: 'fa-comments', studio: 'fa-wand-magic-sparkles' }

const loading = ref(true)
const raw = ref(null)
const levelsRef = ref(null)
const wide = ref(false)
const reduced = ref(false)
const sheet = ref(null)
const keeping = ref(false)
const thanking = ref(null)
const thankNote = ref('')
// "Say how it felt", asked in place in its step: { from: 'step:<key>', reading, … } while open.
const landing = ref(null)
const landedText = ref('')
const landedAck = ref('')
const landedEl = ref(null)
const acting = ref(false)
const claiming = ref('')
const claimingAll = ref(false)
const claimingStudio = ref(false)
const unsealLater = ref(false)
const ceremony = ref(false)
const fx = ref({})
const focus = ref('')
const flashRow = ref('')
const banner = ref(null)
const justDone = ref('')
let timers = []
const later = (ms, fn) => { const t = setTimeout(fn, ms); timers.push(t); return t }

const portrait = computed(() => store.querent_avatar_url || (props.skyGift && props.skyGift.portrait) || '')
const j = computed(() => (raw.value ? readJourney(raw.value, { portrait: portrait.value }) : null))
const lp = computed(() => (j.value && focus.value ? levelPanel(j.value, focus.value) : null))
const tonight = computed(() => (j.value ? tonightCard(j.value) : null))
const finishedNext = computed(() => (j.value && j.value.finished ? nextCard(j.value) : null))
const unsealShown = computed(() => !!(j.value && j.value.unseal && !unsealLater.value && !ceremony.value))
const balance = computed(() => (Number(store.querent_carat_balance) || 0).toLocaleString())

function prefersReduced () {
  try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) } catch (e) { return false }
}
function measureWide () {
  try { wide.value = !props.narrow && window.innerWidth >= 900 } catch (e) { wide.value = false }
}

function setFocus (k) {
  const key = sectionKey(k) || k
  if (!j.value || !levelOrder(j.value).includes(key)) return
  focus.value = key
}
/** A star tapped in the sky: its level in focus, its row picked out in the panel. */
function onSkyStar (key) {
  if (key === 'saturn' || key === 'sa') { openStar('saturn'); return }
  const hit = findStar(j.value, key)
  if (!hit) return
  setFocus(hit.sec.key)
  flashRow.value = key
  nextTick(() => {
    try {
      const row = document.querySelector(`[data-row="${key}"]`)
      if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest', behavior: reduced.value ? 'auto' : 'smooth' })
    } catch (e) { /* */ }
  })
  later(1600, () => { if (flashRow.value === key) flashRow.value = '' })
}

/** The levels drawn now (all their stars lit). */
const drawnNow = (J) => new Set(J ? levelOrder(J).filter((k) => k !== 'sa' && J.sections[k].status === 'done' && J.sections[k].complete) : [])

function apply (d, how = 'step') {
  if (!d || !d.sky) return
  if (typeof d.mystic_entry_carats === 'number') emit('entry-carats', d.mystic_entry_carats)
  const before = drawnNow(j.value)
  const hadSky = !!raw.value
  raw.value = d.sky
  const J = j.value
  emit('unclaimed', J && J.money && J.claim ? J.claim.total : (J && J.welcome ? J.welcome.total : 0))
  if (!focus.value || how === 'open') {
    const hit = props.star && findStar(J, props.star)
    focus.value = hit ? hit.sec.key : focusDefault(J)
  }
  nextTick(() => {
    if (how === 'open') {
      if (props.star && findStar(J, props.star)) {
        fx.value = { ring: props.star }
        later(1800, () => { fx.value = {} })
      }
      playOpenMoments(J)
      return
    }
    // The last star of a level lit here: the banner, then on to the next level (§18).
    const fresh = hadSky ? [...drawnNow(J)].filter((k) => !before.has(k)) : []
    if (fresh.length) completeLevel(J, fresh[fresh.length - 1])
  })
}

/** The line draws in and the level brightens; a banner names what opened; then the next level. */
function completeLevel (J, k) {
  justDone.value = k
  banner.value = levelBanner(J, k)
  later(reduced.value ? BANNER_REDUCED_MS : BANNER_MS, () => {
    banner.value = null
    justDone.value = ''
    const order = levelOrder(J)
    const nextK = order[order.indexOf(k) + 1]
    // The Zodiac Wheel breaks the Sun's seal: its ceremony plays here.
    if (nextK === 'su' && j.value && j.value.unseal) { setFocus('su'); playCeremony(); return }
    if (nextK) setFocus(nextK)
  })
}

// The gift star lands once (the visit after a gift was opened): the star
// ignites and its ✦ rises into the Claim, which counts up to the total. With
// reduced motion it is simply lit and the total is already there.
const giftLanding = ref(false)
const claimShown = computed(() => {
  const J = j.value
  if (!J || !J.claim) return 0
  return giftLanding.value && J.giftStar ? Math.max(0, J.claim.total - J.giftStar.carats) : J.claim.total
})
function landGiftStar (J) {
  const g = J && J.giftStar
  if (!g || g.state !== 'lit' || !g.litAt) return
  let seen = ''
  try { seen = localStorage.getItem(GIFT_STAR_SEEN_KEY) || '' } catch (e) { /* */ }
  if (seen === g.litAt) return
  try { localStorage.setItem(GIFT_STAR_SEEN_KEY, g.litAt) } catch (e) { /* */ }
  if (reduced.value) return
  giftLanding.value = true
  later(1900, () => { giftLanding.value = false })
}

/** On opening: the Sun's ceremony when it is due here; else a reply rings a star once. */
function playOpenMoments (J) {
  if (!J) return
  landGiftStar(J)
  const u = J.unlock
  if (u && sectionKey(u.constellation) === 'su' && J.unseal) { playCeremony(); return }
  const ring = J.rings[0]
  if (ring) {
    const hit = findStar(J, ring.star)
    if (hit) setFocus(hit.sec.key)
    later(reduced.value ? 100 : 900, () => { fx.value = { ring: ring.star, liftWord: ring.word } })
    later(4200, () => { fx.value = { ringDone: ring.star } })
  }
}

/** The Sun rises and breaks the seal; every drawn level brightens; then its card. */
function playCeremony () {
  ceremony.value = true
  setFocus('su')
  later(reduced.value ? 200 : 500, () => { fx.value = { ceremony: true, bright: true } })
  later(reduced.value ? CEREMONY_REDUCED_MS : CEREMONY_MS, () => {
    ceremony.value = false
    fx.value = {}
    track('nav_unlock_seen', { key: 'the_sun' })
    seen('unlock:the_sun')
  })
}

async function seen (key) {
  if (!store.session_id) return
  try {
    const r = await navSeen(store.session_id, key)
    if (r && r.nav) emit('nav', r.nav)
  } catch (e) { /* the moment waits for next time */ }
}

function emitAct (verb) { emit('act', verb) }

/** A button's verb: some are done here (the acts, the claim, the Studio), the rest by ReaderPage. */
function doButton (button, from, starKey = '') {
  const verb = actionFor(button)
  if (!verb) return
  if (from === 'step' || from === 'tonight') track('nav_next', { star: starKey || (from === 'tonight' ? 'tonight_vote' : ''), action: button.kind, stage: props.stage })
  landedAck.value = ''
  if (verb.type === 'landed') { openLanded(verb, starKey || (sheet.value && sheet.value.key) || 'fs_landed'); sheet.value = null; return }
  sheet.value = null
  if (verb.type === 'act') { runAct(verb.act).then((r) => { if (r && r.ok !== false && verb.act === 'birth_time_unknown') keepAfterBirthTime() }); return }
  if (verb.type === 'keep-sky') { keeping.value = true; return }
  if (verb.type === 'thank-mystic') { thanking.value = verb; thankNote.value = ''; return }
  if (verb.type === 'claim-all') { claimAll(); return }
  if (verb.type === 'claim-studio') { openStudio(); return }
  if (verb.type === 'studio') { emit('go-to-studio', verb); return }
  if (verb.type === 'quest') { emit('navigate', { category: verb.category, panel: 'altar' }); return }
  emitAct(verb)
}

// Right after the birth time (or "I don't know it"), the same sheet shows what the sky made (§15).
function keepAfterBirthTime () {
  const hit = findStar(j.value, 'ri_keep')
  if (hit && !hit.star.done) keeping.value = true
}

async function runAct (act, extra = {}) {
  if (acting.value || !store.session_id) return null
  acting.value = true
  try {
    const r = await questAct(store.session_id, act, extra)
    if (r && r.nav) emit('nav', r.nav)
    if (r && r.sky) apply({ sky: r.sky })
    return r
  } catch (e) { return null } finally { acting.value = false }
}
async function keepSky () {
  const r = await runAct('keep_sky')
  if (r && r.ok !== false) keeping.value = false
}
async function sendThanks () {
  const t = thanking.value
  if (!t) return
  const r = await runAct('thank_mystic', { reading_id: t.reading, note: thankNote.value.trim() })
  if (r && r.ok !== false) thanking.value = null
}

function openLanded (verb, key) {
  const hit = findStar(j.value, key)
  if (hit) setFocus(hit.sec.key)
  landing.value = { ...verb, from: `step:${key}` }
  landedText.value = ''
  nextTick(() => {
    try {
      const el = Array.isArray(landedEl.value) ? landedEl.value[0] : landedEl.value
      if (el) el.focus()
    } catch (e) { /* */ }
  })
}
async function saveLanded () {
  const text = landedText.value.trim()
  if (!text || !landing.value) return
  const r = await runAct('landed', { text })
  // Not kept (offline, a 500): the box and their words stay.
  if (!r || r.ok === false) return
  landing.value = null
  landedText.value = ''
  landedAck.value = String(r.line || '').trim()
}

// Another level in focus: an open box goes with it.
watch(focus, () => { if (landing.value) { const k = landing.value.from.slice(5); const hit = findStar(j.value, k); if (!hit || hit.sec.key !== focus.value) landing.value = null } })

function openStar (key) {
  const s = starSheet(j.value, key)
  if (s) sheet.value = s
}

async function load (how = 'step') {
  if (!store.session_id) { loading.value = false; return }
  loading.value = true
  try { apply(await getPlatformQuests(store.session_id), how) } catch (e) { /* */ } finally { loading.value = false }
}

async function claimOne (key) {
  if (claiming.value) return
  claiming.value = key
  try {
    const res = await claimQuest(store.session_id, key)
    if (res && typeof res.balance === 'number') store.querent_carat_balance = res.balance
    apply(res)
    sheet.value = null
  } catch (e) { /* */ } finally { claiming.value = '' }
}
async function claimAll () {
  if (claimingAll.value) return
  claimingAll.value = true
  try {
    const res = await claimAllQuests(store.session_id)
    if (res && typeof res.balance === 'number') store.querent_carat_balance = res.balance
    apply(res)
  } catch (e) { /* */ } finally { claimingAll.value = false }
}

// The Studio is claimed, not handed over: ReaderPage records it and opens the
// Studio entry (its price shown as a goal), then calls studioAccepted().
function openStudio () {
  if (claimingStudio.value) return
  claimingStudio.value = true
  emit('mystic-unlocked')
}
function studioAccepted () {
  claimingStudio.value = false
  load()
}

watch(() => store.session_id, (id, old) => { if (id && !old) load('open') })

onMounted(async () => {
  reduced.value = prefersReduced()
  measureWide()
  window.addEventListener('resize', measureWide)
  await load('open')
  // Opening the Quest clears its dot and makes the fresh rings old.
  if (raw.value) seen('quest')
})
onUnmounted(() => {
  timers.forEach(clearTimeout)
  timers = []
  window.removeEventListener('resize', measureWide)
})
defineExpose({ load, studioAccepted, sky: j, focus })
</script>

<style scoped>
.quest-panel { width: 100%; height: 100%; background: radial-gradient(130% 55% at 50% 0%, #1f103e 0%, #0d0520 62%); color: #efe4ff; padding: 0 16px 84px;
  box-sizing: border-box; display: flex; flex-direction: column; overflow: hidden; }
.quest-panel *, .quest-panel *::before, .quest-panel *::after { box-sizing: border-box; }
.quest-tabs { display: flex; justify-content: center; border-bottom: 1px solid rgba(155,134,201,.18); padding: 8px 16px 0; margin: 0 0 10px; flex: 0 0 auto; }
.quest-tab { position: relative; padding: 0 22px 7px; background: none; border: none; border-bottom: 2px solid #f0d9a3; font: 600 14px Cinzel, serif; letter-spacing: .16em; color: #f0d9a3; cursor: default; }
.quest-panel.wide .quest-tabs { display: none; }
.quest-tab::after { content: ''; position: absolute; inset: -10px 0; }
.quest-loading { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 40px 0; color: #9b86c9; font-size: 13px; }
.q-pulse { width: 10px; height: 10px; border-radius: 50%; background: #c9a94e; animation: qp-pulse 1.2s ease-in-out infinite; }
@keyframes qp-pulse { 0%, 100% { opacity: .3 } 50% { opacity: 1 } }

.qp-body { position: relative; flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; gap: 14px; max-width: 520px; width: 100%; margin: 0 auto;
  overflow-y: auto; scrollbar-width: none; padding-bottom: 12px; }
.qp-body::-webkit-scrollbar { display: none; }
.qp-left { flex: 0 0 auto; position: relative; }
.qp-side { flex: 0 0 auto; display: flex; flex-direction: column; gap: 12px; position: relative; }
.quest-panel.wide { padding-top: 14px; }
/* One level in focus on the left, its steps on the right (§18); on a phone the steps sit below. */
.quest-panel.wide .qp-body { max-width: 1060px; display: grid; grid-template-columns: minmax(0, 1fr) 400px; gap: 22px; align-items: start; overflow: hidden; height: 100%; }
.quest-panel.wide .qp-left { position: sticky; top: 0; }
.quest-panel.wide .qp-side { max-height: 100%; overflow-y: auto; scrollbar-width: thin; padding-bottom: 20px; }
.qp-eyebrow { font: 600 10px Cinzel, serif; letter-spacing: .2em; text-transform: uppercase; color: #f0d9a3; }
.qp-dot { font-style: normal; color: #8e7bb5; margin: 0 4px; }
.qp-next { border: 1px solid rgba(201,169,78,.45); border-radius: 18px; padding: 12px 16px 13px; background: linear-gradient(170deg, #28164a, #170b2e); }
.qp-next-title { font: 500 22px/1.12 'Cormorant Garamond', Cormorant, serif; margin: 4px 0 3px; color: #fff; }
.quest-panel.wide .qp-next-title { font-size: 23px; }
.qp-next-body { margin: 0 0 9px; color: #b9a6d6; font-size: 13px; line-height: 1.45; }
.qp-later { margin: 2px 0 0; font: italic 16px 'Cormorant Garamond', Cormorant, serif; color: #f0d9a3; }
.qp-btn { display: block; width: 100%; min-height: 44px; border: 0; border-radius: 14px; padding: 12px 14px; cursor: pointer; background: linear-gradient(#f3dfa8, #c9a94e);
  color: #22133b; font: 600 13px Cinzel, serif; letter-spacing: .1em; text-transform: uppercase; box-shadow: 0 6px 18px rgba(201,169,78,.25); }
.qp-btn:disabled { opacity: .6; }
.qp-btn:focus-visible, .qp-quiet:focus-visible, .qp-sg:focus-visible { outline: 2px solid #ffe7b5; outline-offset: 2px; }
.qp-btn-inline { display: inline-block; width: auto; padding: 9px 26px; }
.qp-quiet { display: block; margin: 8px auto 0; background: none; border: 0; color: #b9a6d6; font: italic 16px 'Cormorant Garamond', Cormorant, serif;
  text-decoration: underline; text-underline-offset: 3px; cursor: pointer; min-height: 32px; }

.qp-welcome { margin-bottom: 10px; border-radius: 18px; padding: 13px 16px; text-align: center; border: 1px solid rgba(240,217,163,.5);
  background: radial-gradient(120% 120% at 50% 0%, rgba(240,217,163,.16), rgba(240,217,163,.03) 70%); }
.qp-amt { font: 500 34px/1.1 'Cormorant Garamond', Cormorant, serif; color: #ffe7b5; margin: 2px 0 8px; }
.qp-claimrow { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 10px; padding: 8px 10px 8px 12px; border-radius: 12px;
  border: 1px solid rgba(201,169,78,.35); background: rgba(20,8,40,.72); font: italic 15px 'Cormorant Garamond', Cormorant, serif; color: #e6dafa; }
.qp-claim { flex: 0 0 auto; min-height: 36px; padding: 6px 12px; border: none; border-radius: 9px; cursor: pointer; font: 600 13px Cinzel, serif; color: #1a0f2e;
  background: linear-gradient(180deg, #f0d9a3, #c9a94e); }

.qp-unseal { text-align: center; padding: 16px 20px 14px; border-radius: 22px; background: radial-gradient(120% 90% at 50% 0%, #2b1752, #150a2b);
  border: 1px solid rgba(240,217,163,.55); box-shadow: 0 0 50px rgba(240,217,163,.16); animation: qp-rise .7s cubic-bezier(.2,.8,.2,1) both; }
.qp-unseal-sun { width: 64px; height: 64px; }
.qp-unseal-title { font: 500 27px/1.1 'Cormorant Garamond', Cormorant, serif; margin: 4px 0; color: #fff; }
.qp-unseal-body { color: #b9a6d6; margin: 0 0 12px; font-size: 14px; }
@keyframes qp-rise { from { transform: translateY(24px); opacity: 0; } }

/* A level drawn: the banner names what opened (§18). */
.qp-banner { position: absolute; left: 50%; bottom: 22px; transform: translateX(-50%); z-index: 6; width: min(420px, calc(100% - 28px)); padding: 14px 18px; border-radius: 16px;
  text-align: center; background: radial-gradient(120% 120% at 50% 0%, #3a2266, #1a0d33); border: 1px solid rgba(240,217,163,.65); box-shadow: 0 0 40px rgba(240,217,163,.22);
  animation: qp-rise .6s cubic-bezier(.2,.8,.2,1) both; }
.qp-banner b { display: block; margin: 4px 0 2px; font: 500 22px 'Cormorant Garamond', Cormorant, serif; color: #fff; }
.qp-banner p { margin: 0; font: 500 14px Inter, system-ui, sans-serif; color: #e3d8f5; }
.qp-banner-ct { display: inline-block; margin-top: 8px; padding: 4px 12px; border-radius: 999px; background: rgba(240,217,163,.14); border: 1px solid rgba(240,217,163,.5); color: #ffe7b5; font: 700 14px Inter, system-ui, sans-serif; }
/* Tonight · Cast your star. */
.qp-card { border: 1px solid rgba(240,217,163,.6); border-radius: 18px; padding: 14px 16px; background: linear-gradient(170deg, #322056, #1a0d33); }
.qp-card-title { margin: 4px 0; font: 500 22px 'Cormorant Garamond', Cormorant, serif; color: #fff; }
.qp-card-body { margin: 0 0 10px; font: 400 14px/1.45 Inter, system-ui, sans-serif; color: #d8c9f2; }
/* The focused level's panel. */
.qp-level { border: 1px solid rgba(201,169,78,.45); border-radius: 18px; padding: 16px 16px 14px; background: linear-gradient(170deg, #28164a, #170b2e); }
.qp-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; }
.qp-bal { padding: 3px 10px; border-radius: 999px; border: 1px solid rgba(240,217,163,.45); color: #ffe7b5; font: 700 13px Inter, system-ui, sans-serif; }
.qp-level-name { margin: 4px 0 2px; font: 500 28px/1.1 'Cormorant Garamond', Cormorant, serif; color: #fff; }
.qp-level-line { margin: 0 0 12px; font: 500 14px Inter, system-ui, sans-serif; color: #c4b6de; }
.qp-unl { display: grid; grid-template-columns: 40px 1fr auto; gap: 10px; align-items: center; padding: 10px 12px; border-radius: 14px; background: rgba(10,4,24,.45);
  border: 1px solid rgba(240,217,163,.25); margin-bottom: 10px; }
.qp-unl-ic { width: 40px; height: 40px; border-radius: 12px; display: grid; place-items: center; background: #663399; color: #fff; font-size: 17px; }
.qp-unl b { display: block; font: 600 10px Cinzel, serif; letter-spacing: .18em; color: #e7c983; text-transform: uppercase; }
.qp-unl-t { font: 600 15px Inter, system-ui, sans-serif; color: #fff; }
.qp-unl-s { font: 600 12px Inter, system-ui, sans-serif; color: #b9abd3; text-align: right; white-space: nowrap; }
.qp-unl-s.ok { color: #ffe7b5; }
.qp-rew { padding: 10px 12px; border-radius: 14px; background: rgba(240,217,163,.07); border: 1px dashed rgba(240,217,163,.4); margin-bottom: 10px;
  font: 500 13.5px/1.45 Inter, system-ui, sans-serif; color: #e9dcff; }
.qp-steps { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
.qs { display: grid; grid-template-columns: 26px 1fr; gap: 4px 10px; padding: 10px; border-radius: 14px; border: 1px solid rgba(155,134,201,.2); background: rgba(10,4,24,.3); transition: box-shadow .4s; }
.qs-n { width: 24px; height: 24px; border-radius: 50%; display: grid; place-items: center; font: 700 11px Inter, system-ui, sans-serif; border: 1px solid rgba(203,184,238,.6); color: #cbb8ee; }
.qs.lit .qs-n { background: #f0d9a3; border-color: #f0d9a3; color: #1a0a2e; }
.qs.next { border-color: rgba(240,217,163,.55); background: rgba(240,217,163,.06); }
.qs.next .qs-n { border-color: #f0d9a3; color: #ffe7b5; box-shadow: 0 0 10px rgba(240,217,163,.5); }
.qs.flash { box-shadow: 0 0 0 2px #ffe7b5, 0 0 18px rgba(255,231,181,.35); }
.qs-main b { display: block; font: 600 15px Inter, system-ui, sans-serif; color: #fff; }
.qs.not .qs-main b { color: #cdbfe6; }
.qs.lit .qs-main b::before { content: '✓ '; color: #f0d9a3; }
.qs-main p { margin: 2px 0 0; font: 400 13.5px/1.4 Inter, system-ui, sans-serif; color: #b9abd3; }
.qs-main small { display: block; margin-top: 2px; font: italic 500 15px 'Cormorant Garamond', Cormorant, serif; color: #e7c983; }
.qs-main .qs-sub { font-style: italic; color: #e7c983; }
.qs-open { display: block; width: 100%; background: none; border: 0; padding: 0; text-align: left; cursor: pointer; color: inherit; }
.qs-open:focus-visible { outline: 2px solid #ffe7b5; outline-offset: 2px; border-radius: 6px; }
.qs-act { grid-column: 2; display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 6px; }
.qs-act .qp-landed { flex: 1 1 100%; }
.qp-btn.qs-go { display: inline-block; width: auto; min-height: 40px; padding: 8px 18px; font-size: 12.5px; }
.qp-btn.qs-go.quiet { background: none; color: #f0d9a3; border: 1px solid rgba(240,217,163,.55); box-shadow: none; }
.qp-quiet.qs-quiet { display: inline-block; margin: 0; font-size: 15px; }
.qs-later { font: italic 16px 'Cormorant Garamond', Cormorant, serif; color: #f0d9a3; }
.qs-not { font: 500 13px Inter, system-ui, sans-serif; color: #9b86c9; }
.qs-coin { padding: 2px 8px; border-radius: 999px; border: 1px solid rgba(240,217,163,.45); color: #ffe7b5; font: 700 12px Inter, system-ui, sans-serif; }
.qs.lit .qs-coin { background: rgba(240,217,163,.14); }
.qp-along { margin: 12px 0 6px; font: 600 10px Cinzel, serif; letter-spacing: .2em; color: rgba(240,217,163,.7); text-transform: uppercase; }
.qp-nav { display: flex; justify-content: space-between; margin-top: 12px; }
.qp-nav button { background: none; border: 0; color: #d8c9f2; font: 600 13px Inter, system-ui, sans-serif; cursor: pointer; min-height: 36px; padding: 0 4px; }
.qp-nav button:disabled { opacity: .3; cursor: default; }
.qp-nav button:focus-visible { outline: 2px solid #ffe7b5; outline-offset: 2px; }
.qp-level.teaser .qp-coming { margin: 4px 0 8px; }
/* Sheets. */
.qp-sheet-wrap { position: fixed; inset: 0; z-index: 1000; display: flex; align-items: flex-end; justify-content: center; background: rgba(5,2,12,.5); }
.quest-panel.wide .qp-sheet-wrap { align-items: center; }
.qp-sheet { position: relative; width: 100%; max-width: 520px; max-height: 80%; overflow-y: auto; background: linear-gradient(180deg, #22113f, #160a2c);
  border-radius: 26px 26px 0 0; border-top: 1px solid rgba(201,169,78,.55); padding: 18px 20px calc(26px + 70px); animation: qp-up .34s cubic-bezier(.2,.8,.2,1) both; }
.quest-panel.wide .qp-sheet { width: 460px; border-radius: 22px; border: 1px solid rgba(201,169,78,.45); padding-bottom: 26px; }
@keyframes qp-up { from { transform: translateY(40px); opacity: 0; } }
.qp-sheet-x { position: absolute; top: 10px; right: 12px; width: 36px; height: 36px; border: 0; background: none; color: #b9a6d6; font-size: 24px; line-height: 1; cursor: pointer; }
.qp-sheet-title { font: 500 25px/1.15 'Cormorant Garamond', Cormorant, serif; margin: 4px 34px 10px 0; color: #fff; }
.qp-sline { margin: 0 0 12px; font: 400 18px/1.35 'Cormorant Garamond', Cormorant, serif; color: #e6dafa; }
.qp-sub-line { font-style: italic; color: #f0d9a3; }
.qp-mo { border-radius: 16px; padding: 13px 15px 14px; margin: 4px 0 10px; background: radial-gradient(120% 120% at 0% 0%, rgba(240,217,163,.09), rgba(240,217,163,.02) 60%); border: 1px solid rgba(240,217,163,.2); }
.qp-mo-date { font: 600 9.5px Cinzel, serif; letter-spacing: .18em; color: #8e7bb5; text-transform: uppercase; margin-bottom: 5px; }
.qp-mo-line { font: 400 20px/1.32 'Cormorant Garamond', Cormorant, serif; color: #efe4ff; }
.qp-mo-line i { color: #f0d9a3; }
.qp-mo-line b { font-weight: 600; color: #fff; }
.qp-mo-quote { margin: 9px 0 0; padding: 2px 0 2px 12px; border-left: 2px solid rgba(201,169,78,.6); font: italic 19px/1.3 'Cormorant Garamond', Cormorant, serif; color: #ffe7b5; }
.qp-ringline { margin: 0 0 8px; font: 400 17px 'Cormorant Garamond', Cormorant, serif; color: #e6dafa; }
.qp-ringline i { color: #ffe7b5; }
.qp-only { margin: 10px 0 0; text-align: center; font: italic 14px 'Cormorant Garamond', Cormorant, serif; color: #8e7bb5; }
.qp-coming { list-style: none; padding: 0; margin: 4px 0 0; display: flex; flex-wrap: wrap; gap: 6px; }
.qp-coming li { border: 1px dashed rgba(155,134,201,.5); border-radius: 99px; padding: 4px 11px; font: italic 16px 'Cormorant Garamond', Cormorant, serif; color: #b9a6d6; }
.qp-gift { display: flex; flex-direction: column; align-items: center; gap: 6px; margin: 4px 0 12px; }
.qp-gift-por { width: 92px; height: 92px; border-radius: 50%; object-fit: cover; box-shadow: 0 0 0 2px #f0d9a3; }
/* The portrait still painting (§20.4): a soft shimmer in its circle. */
.qp-gift-por.painting { display: block; background: radial-gradient(circle at 40% 35%, #4a2d7a, #1d0f38); animation: qp-paint 1.8s ease-in-out infinite; }
@keyframes qp-paint { 0%, 100% { opacity: .55; } 50% { opacity: 1; } }
.qp-gift-name { font: 500 30px 'Cormorant Garamond', Cormorant, serif; color: #fff; }
.qp-chips { display: flex; flex-wrap: wrap; gap: 6px; margin: 0 0 8px; }
.qp-chip { border: 1px solid rgba(240,217,163,.4); background: none; color: #f0d9a3; border-radius: 99px; padding: 6px 12px; font: italic 16px 'Cormorant Garamond', Cormorant, serif; cursor: pointer; }
.qp-chip.on { background: rgba(240,217,163,.15); }
.qp-landed { margin: 2px 0 0; }
.qp-landed-ack { margin: 10px 2px 0; font: italic 17px/1.4 'Cormorant Garamond', Cormorant, serif; color: #f0d9a3; }
.qp-note { width: 100%; margin: 0 0 4px; border-radius: 12px; border: 1px solid rgba(155,134,201,.35); background: rgba(10,4,24,.6); color: #efe4ff; padding: 8px 10px; font: 400 16px 'Cormorant Garamond', Cormorant, serif; resize: vertical; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
@media (prefers-reduced-motion: reduce) {
  .qp-unseal, .qp-sheet { animation: qp-fade .4s ease-out both; }
  .qp-gift-por.painting, .qp-banner { animation: none; }
}
@keyframes qp-fade { from { opacity: 0; } }
</style>
